import {
  CAMERA_MAX_PAN_BASE,
  FEEDBACK_BASE_SCALE,
  LOCK_WORLD_ZOOM,
  MENU_SCALE_FINAL,
  MINI_CLOCK_SCALE,
  INSTALLER_SCANNER_DIM_STRENGTH_DEFAULT,
} from './config';
import { OneEuroFilter } from './utils/OneEuroFilter';
import type { Item, InstallConveyor, InstallerFlow, AppState, LockLineSegment, ScannerMorphAnim, HomeUIState } from './types';
import type { WatchfaceHost } from './watchface/host';
import type { Quat } from './utils/quaternion';

export const state = {
  // apps
  items: [] as Item[],
  highlighted: null as Item | null,
  installedProjectIds: new Set<string>(),
  ringGuideEls: [] as HTMLElement[],

  // installer
  installerItem: null as Item | null,
  installerSlotIndex: -1,
  installConveyor: null as InstallConveyor | null,
  installerFlow: null as InstallerFlow | null,
  scannerMediaStream: null as MediaStream | null,
  scannerMediaRequestId: 0,
  scannerMorphAnim: null as ScannerMorphAnim | null,

  // camera
  cameraNormX: 0,
  cameraNormY: 0,
  cameraTargetX: 0,
  cameraTargetY: 0,
  cameraX: 0,
  cameraY: 0,
  cameraMaxPanCurrent: CAMERA_MAX_PAN_BASE,

  // input
  inputActive: false,
  refQuat: null as Quat | null,
  homeRefQuat: null as Quat | null,

  // top-level app state
  appState: 'lock' as AppState,

  // watchface
  watchfaceHost: null as WatchfaceHost | null,

  // lock screen
  unlockKeyTimes: [] as number[],
  feedbackScaleCurrent: FEEDBACK_BASE_SCALE,
  feedbackScaleTarget: FEEDBACK_BASE_SCALE,
  feedbackVelocity: 0,
  feedbackLastPressAt: 0,
  unlockStartTime: 0,
  lockingStartTime: 0,
  lockingStartCameraX: 0,
  lockingStartCameraY: 0,
  lockOverlayPendingHide: false,
  persistentClockScale: MINI_CLOCK_SCALE,
  // Press-feedback multiplier for the center clock (lockScreen). Driven
  // by the pressFeedback module; mainLoop multiplies it into the rendered
  // clock scale. 1 = no press, ~0.6 = pressed.
  clockPressScale: 1,
  worldZoom: LOCK_WORLD_ZOOM,
  menuScale: MENU_SCALE_FINAL,
  parallaxX: 0,
  parallaxY: 0,
  parallaxTargetX: 0,
  parallaxTargetY: 0,
  lockLineSegments: [] as LockLineSegment[],
  lockHourItems: null as Item[] | null,

  // installer scanner
  installerScannerDimStrength: INSTALLER_SCANNER_DIM_STRENGTH_DEFAULT,
  installerScannerLineTint: 0,

  // home options/edit mode
  homeUI: {
    mode: 'normal',
    targetItem: null,
    optionIndex: 0,
    enterStartedAt: 0,
    exitStartedAt: 0,
    exitAction: null,
    hiddenItems: [],
    overlayOpacity: 0,
    lineOpacity: 1,
    zoomBlend: 0,
    rollRefQuat: null,
    lastTwistAt: 0,
    twistNeutralLatched: true,
    optionsInputPhase: 0,
    dragActive: false,
    dragItem: null,
    dragTargetSlot: -1,
    dragWorldX: 0,
    dragWorldY: 0,
    dragLockedScale: 0,
    removingItem: null,
    optionsSnapshot: [],
  } as HomeUIState,
};

export const filters = {
  inputX: new OneEuroFilter(60, 3.0, 1.5),
  inputY: new OneEuroFilter(60, 3.0, 1.5),
  cameraX: new OneEuroFilter(60, 3.0, 1.5, 1.0),
  cameraY: new OneEuroFilter(60, 3.0, 1.5, 1.0),
  homeRoll: new OneEuroFilter(60, 3.0, 1.5, 1.0),
};
